/**
 * Asset Mix Optimizer Component
 * Multi-modal route planning with vehicle type selection and optimization
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Switch,
  FormControlLabel,
  FormGroup,
  Tooltip,
  Alert,
  AlertTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import {
  LocalShipping,
  DirectionsCar,
  TwoWheeler,
  DirectionsWalk,
  Flight,
  DirectionsBoat,
  Train,
  AttachMoney,
  Speed,
  AccessTime,
  LocalGasStation,
  Nature,
  Warning,
  CheckCircle,
  Info,
  ExpandMore,
  Settings,
  Calculate,
  CompareArrows,
  TrendingUp,
  ShowChart,
  Timeline,
  Assessment,
  Help,
  Add,
  Remove,
  Edit,
  Delete,
  ContentCopy,
  Save,
  PlayArrow,
  Inventory,
  Scale,
  Category,
} from '@mui/icons-material';
import { useVehicles } from '../../hooks/useVehicles';
import { useJobs } from '../../hooks/useJobs';
import { useProject } from '../../contexts/ProjectContext';
import type { Vehicle, Job, VehicleProfile } from '../../types/vrp';

interface VehicleType {
  id: string;
  name: string;
  profile: VehicleProfile;
  icon: React.ReactNode;
  capacity: {
    weight: number;
    volume: number;
    units?: number;
  };
  costs: {
    fixed: number;
    perKm: number;
    perHour: number;
    perStop?: number;
  };
  constraints: {
    maxDistance?: number;
    maxDuration?: number;
    maxStops?: number;
    requiredSkills?: number[];
  };
  emissions: {
    co2PerKm: number;
    fuelType: string;
  };
  availability: number;
  selected: number;
}

interface OptimizationCriteria {
  costWeight: number;
  timeWeight: number;
  emissionsWeight: number;
  utilizationWeight: number;
}

interface OptimizationResult {
  vehicleMix: {
    type: VehicleType;
    count: number;
    routes: number;
    utilization: number;
  }[];
  metrics: {
    totalCost: number;
    totalTime: number;
    totalDistance: number;
    totalEmissions: number;
    avgUtilization: number;
  };
  comparison: {
    privateFleetCost: number;
    carrierCost: number;
    directShippingCost: number;
    savings: number;
  };
  recommendations: string[];
}

interface AssetMixOptimizerProps {
  onOptimize?: (result: OptimizationResult) => void;
  preselectedVehicles?: Vehicle[];
  jobsToOptimize?: Job[];
}

export const AssetMixOptimizer: React.FC<AssetMixOptimizerProps> = ({
  onOptimize,
  preselectedVehicles,
  jobsToOptimize,
}) => {
  // Data hooks
  const { currentProject } = useProject();
  const { data: vehicles = [], isLoading: vehiclesLoading } = useVehicles(currentProject?.id || 0);
  const { data: jobs = [], isLoading: jobsLoading } = useJobs(currentProject?.id || 0);

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([
    {
      id: 'truck-53',
      name: "53' Truck",
      profile: 'driving',
      icon: <LocalShipping />,
      capacity: { weight: 20000, volume: 3000, units: 26 },
      costs: { fixed: 150, perKm: 2.5, perHour: 45, perStop: 15 },
      constraints: { maxDistance: 500, maxDuration: 660 },
      emissions: { co2PerKm: 0.95, fuelType: 'Diesel' },
      availability: 10,
      selected: 0,
    },
    {
      id: 'truck-26',
      name: "26' Truck",
      profile: 'driving',
      icon: <LocalShipping style={{ fontSize: 20 }} />,
      capacity: { weight: 10000, volume: 1500, units: 14 },
      costs: { fixed: 100, perKm: 1.8, perHour: 35, perStop: 10 },
      constraints: { maxDistance: 300, maxDuration: 480 },
      emissions: { co2PerKm: 0.65, fuelType: 'Diesel' },
      availability: 15,
      selected: 0,
    },
    {
      id: 'van',
      name: 'Delivery Van',
      profile: 'driving',
      icon: <DirectionsCar />,
      capacity: { weight: 2000, volume: 300, units: 8 },
      costs: { fixed: 50, perKm: 1.2, perHour: 25, perStop: 5 },
      constraints: { maxDistance: 200, maxDuration: 480 },
      emissions: { co2PerKm: 0.25, fuelType: 'Gasoline' },
      availability: 20,
      selected: 0,
    },
    {
      id: 'bike',
      name: 'Cargo Bike',
      profile: 'cycling',
      icon: <TwoWheeler />,
      capacity: { weight: 100, volume: 50, units: 4 },
      costs: { fixed: 20, perKm: 0.5, perHour: 20, perStop: 2 },
      constraints: { maxDistance: 50, maxDuration: 360 },
      emissions: { co2PerKm: 0, fuelType: 'Electric' },
      availability: 10,
      selected: 0,
    },
  ]);

  const [criteria, setCriteria] = useState<OptimizationCriteria>({
    costWeight: 40,
    timeWeight: 30,
    emissionsWeight: 20,
    utilizationWeight: 10,
  });

  const [deliveryMode, setDeliveryMode] = useState<'private' | 'carrier' | 'mixed'>('mixed');
  const [optimizationRunning, setOptimizationRunning] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState<OptimizationResult | null>(null);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);

  // Carrier options
  const [carrierOptions] = useState([
    { id: 'ltl', name: 'LTL Carrier', costPerKg: 0.15, transitDays: 3 },
    { id: 'ftl', name: 'FTL Carrier', costPerLoad: 1200, transitDays: 2 },
    { id: 'parcel', name: 'Parcel Service', costPerPackage: 15, transitDays: 1 },
    { id: 'expedited', name: 'Expedited Service', costMultiplier: 2.5, transitDays: 0.5 },
  ]);

  // Calculate total demand
  const totalDemand = useMemo(() => {
    const selectedJobs = jobsToOptimize || jobs;
    return {
      weight: selectedJobs.reduce((sum, job) => sum + (job.delivery?.[0] || 0), 0),
      volume: selectedJobs.reduce((sum, job) => sum + (job.delivery?.[1] || 0), 0),
      stops: selectedJobs.length,
      serviceTime: selectedJobs.reduce((sum, job) => sum + job.service + job.setup, 0),
    };
  }, [jobs, jobsToOptimize]);

  // Handle vehicle type selection
  const handleVehicleTypeChange = (typeId: string, count: number) => {
    setVehicleTypes(prev =>
      prev.map(type =>
        type.id === typeId
          ? { ...type, selected: Math.min(count, type.availability) }
          : type
      )
    );
  };

  // Run optimization
  const runOptimization = async () => {
    setOptimizationRunning(true);
    
    // Simulate optimization process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Calculate optimized vehicle mix (simplified simulation)
    const optimizedMix = vehicleTypes.map(type => {
      const utilization = Math.min(
        (totalDemand.weight / type.capacity.weight) * 100,
        (totalDemand.volume / type.capacity.volume) * 100,
        100
      );
      
      const optimalCount = Math.ceil(
        totalDemand.weight / type.capacity.weight / (utilization / 100)
      );
      
      return {
        type,
        count: Math.min(optimalCount, type.availability),
        routes: Math.ceil(totalDemand.stops / (type.constraints.maxStops || 20)),
        utilization,
      };
    });

    // Calculate metrics
    const metrics = {
      totalCost: optimizedMix.reduce((sum, mix) => 
        sum + (mix.count * mix.type.costs.fixed) + 
        (mix.routes * 100 * mix.type.costs.perKm), 0
      ),
      totalTime: totalDemand.serviceTime + (optimizedMix.length * 60),
      totalDistance: optimizedMix.reduce((sum, mix) => sum + (mix.routes * 100), 0),
      totalEmissions: optimizedMix.reduce((sum, mix) => 
        sum + (mix.routes * 100 * mix.type.emissions.co2PerKm), 0
      ),
      avgUtilization: optimizedMix.reduce((sum, mix) => sum + mix.utilization, 0) / optimizedMix.length,
    };

    // Calculate comparison
    const comparison = {
      privateFleetCost: metrics.totalCost,
      carrierCost: totalDemand.weight * 0.15 * 1000, // LTL rate
      directShippingCost: totalDemand.stops * 50, // Direct shipping rate
      savings: 0,
    };
    comparison.savings = Math.min(comparison.carrierCost, comparison.directShippingCost) - comparison.privateFleetCost;

    // Generate recommendations
    const recommendations = [];
    if (comparison.savings > 0) {
      recommendations.push('Private fleet is more cost-effective than carrier options');
    }
    if (metrics.avgUtilization < 70) {
      recommendations.push('Consider consolidating routes to improve vehicle utilization');
    }
    if (metrics.totalEmissions > 1000) {
      recommendations.push('Consider adding electric vehicles to reduce emissions');
    }
    
    const result: OptimizationResult = {
      vehicleMix: optimizedMix.filter(mix => mix.count > 0),
      metrics,
      comparison,
      recommendations,
    };
    
    setOptimizationResult(result);
    setOptimizationRunning(false);
    onOptimize?.(result);
  };

  // Render vehicle type card
  const renderVehicleTypeCard = (type: VehicleType) => (
    <Card key={type.id} elevation={2}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: 1, 
            backgroundColor: 'primary.light',
            color: 'primary.main',
            mr: 2,
          }}>
            {type.icon}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">{type.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {type.profile === 'driving' ? 'Road' : type.profile === 'cycling' ? 'Bike' : 'Walking'}
            </Typography>
          </Box>
          <Chip
            label={`${type.selected}/${type.availability}`}
            color={type.selected > 0 ? 'primary' : 'default'}
            size="small"
          />
        </Box>

        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6 }}>
            <Typography variant="caption" color="text.secondary">Capacity</Typography>
            <Typography variant="body2">
              {type.capacity.weight.toLocaleString()}kg
            </Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="caption" color="text.secondary">Volume</Typography>
            <Typography variant="body2">
              {type.capacity.volume.toLocaleString()}m³
            </Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="caption" color="text.secondary">Fixed Cost</Typography>
            <Typography variant="body2">
              ${type.costs.fixed}/day
            </Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="caption" color="text.secondary">Per Km</Typography>
            <Typography variant="body2">
              ${type.costs.perKm}/km
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleVehicleTypeChange(type.id, type.selected - 1)}
            disabled={type.selected === 0}
          >
            <Remove />
          </IconButton>
          <TextField
            size="small"
            type="number"
            value={type.selected}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleVehicleTypeChange(type.id, parseInt(e.target.value) || 0)}
            inputProps={{ 
              min: 0, 
              max: type.availability,
              style: { textAlign: 'center', width: 60 }
            }}
          />
          <IconButton
            size="small"
            onClick={() => handleVehicleTypeChange(type.id, type.selected + 1)}
            disabled={type.selected >= type.availability}
          >
            <Add />
          </IconButton>
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Chip
          icon={<Nature />}
          label={`${type.emissions.co2PerKm}kg CO₂/km`}
          size="small"
          color={type.emissions.co2PerKm === 0 ? 'success' : 'default'}
        />
        <Button size="small" startIcon={<Info />}>
          Details
        </Button>
      </CardActions>
    </Card>
  );

  const steps = [
    'Define Requirements',
    'Select Vehicle Types',
    'Set Optimization Criteria',
    'Review & Optimize',
  ];

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Category sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" fontWeight={600}>
              Asset Mix Optimizer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Optimize your fleet composition for cost, time, and sustainability
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<CompareArrows />}
            onClick={() => setCompareDialogOpen(true)}
          >
            Compare Options
          </Button>
        </Box>

        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>Define Requirements</StepLabel>
            <StepContent>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>Demand Summary</AlertTitle>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2">
                          <strong>Total Weight:</strong> {totalDemand.weight.toLocaleString()}kg
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2">
                          <strong>Total Volume:</strong> {totalDemand.volume.toLocaleString()}m³
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2">
                          <strong>Delivery Stops:</strong> {totalDemand.stops}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2">
                          <strong>Service Time:</strong> {Math.round(totalDemand.serviceTime / 60)}h
                        </Typography>
                      </Grid>
                    </Grid>
                  </Alert>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl component="fieldset">
                    <FormLabel>Delivery Mode</FormLabel>
                    <RadioGroup
                      value={deliveryMode}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeliveryMode(e.target.value as 'private' | 'carrier' | 'mixed')}
                    >
                      <FormControlLabel 
                        value="private" 
                        control={<Radio />} 
                        label="Private Fleet Only" 
                      />
                      <FormControlLabel 
                        value="carrier" 
                        control={<Radio />} 
                        label="Carrier Services Only" 
                      />
                      <FormControlLabel 
                        value="mixed" 
                        control={<Radio />} 
                        label="Mixed Mode (Recommended)" 
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(1)}
                >
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Select Vehicle Types</StepLabel>
            <StepContent>
              <Grid container spacing={2}>
                {vehicleTypes.map(type => (
                  <Grid key={type.id} size={{ xs: 12, sm: 6, md: 3 }}>
                    {renderVehicleTypeCard(type)}
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                <Button onClick={() => setActiveStep(0)}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(2)}
                  disabled={!vehicleTypes.some(t => t.selected > 0)}
                >
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Set Optimization Criteria</StepLabel>
            <StepContent>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Optimization Weights
                  </Typography>
                  <Box sx={{ px: 2 }}>
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Cost</Typography>
                        <Typography variant="body2">{criteria.costWeight}%</Typography>
                      </Box>
                      <Slider
                        value={criteria.costWeight}
                        onChange={(_: Event, value: number | number[]) => setCriteria({ ...criteria, costWeight: value as number })}
                        marks
                        step={10}
                        min={0}
                        max={100}
                      />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Time</Typography>
                        <Typography variant="body2">{criteria.timeWeight}%</Typography>
                      </Box>
                      <Slider
                        value={criteria.timeWeight}
                        onChange={(_: Event, value: number | number[]) => setCriteria({ ...criteria, timeWeight: value as number })}
                        marks
                        step={10}
                        min={0}
                        max={100}
                      />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Emissions</Typography>
                        <Typography variant="body2">{criteria.emissionsWeight}%</Typography>
                      </Box>
                      <Slider
                        value={criteria.emissionsWeight}
                        onChange={(_: Event, value: number | number[]) => setCriteria({ ...criteria, emissionsWeight: value as number })}
                        marks
                        step={10}
                        min={0}
                        max={100}
                        color="success"
                      />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Utilization</Typography>
                        <Typography variant="body2">{criteria.utilizationWeight}%</Typography>
                      </Box>
                      <Slider
                        value={criteria.utilizationWeight}
                        onChange={(_: Event, value: number | number[]) => setCriteria({ ...criteria, utilizationWeight: value as number })}
                        marks
                        step={10}
                        min={0}
                        max={100}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showAdvancedSettings}
                        onChange={(e) => setShowAdvancedSettings(e.target.checked)}
                      />
                    }
                    label="Advanced Settings"
                  />
                  <Collapse in={showAdvancedSettings}>
                    <Box sx={{ mt: 2 }}>
                      <FormGroup>
                        <FormControlLabel
                          control={<Switch defaultChecked />}
                          label="Consider traffic patterns"
                        />
                        <FormControlLabel
                          control={<Switch defaultChecked />}
                          label="Include driver breaks"
                        />
                        <FormControlLabel
                          control={<Switch />}
                          label="Allow overnight routes"
                        />
                        <FormControlLabel
                          control={<Switch />}
                          label="Prioritize green vehicles"
                        />
                      </FormGroup>
                    </Box>
                  </Collapse>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                <Button onClick={() => setActiveStep(1)}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(3)}
                >
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Review & Optimize</StepLabel>
            <StepContent>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <AlertTitle>Ready to Optimize</AlertTitle>
                    Your configuration is complete. Click "Run Optimization" to find the optimal vehicle mix.
                  </Alert>
                  
                  {optimizationResult && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Optimization Results
                      </Typography>
                      
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Card>
                            <CardContent>
                              <Typography color="text.secondary" gutterBottom>
                                Total Cost
                              </Typography>
                              <Typography variant="h5">
                                ${optimizationResult.metrics.totalCost.toLocaleString()}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Card>
                            <CardContent>
                              <Typography color="text.secondary" gutterBottom>
                                Total Time
                              </Typography>
                              <Typography variant="h5">
                                {Math.round(optimizationResult.metrics.totalTime / 60)}h
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Card>
                            <CardContent>
                              <Typography color="text.secondary" gutterBottom>
                                CO₂ Emissions
                              </Typography>
                              <Typography variant="h5">
                                {optimizationResult.metrics.totalEmissions.toFixed(1)}kg
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                          <Card>
                            <CardContent>
                              <Typography color="text.secondary" gutterBottom>
                                Avg Utilization
                              </Typography>
                              <Typography variant="h5">
                                {optimizationResult.metrics.avgUtilization.toFixed(0)}%
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Vehicle Type</TableCell>
                              <TableCell align="center">Count</TableCell>
                              <TableCell align="center">Routes</TableCell>
                              <TableCell align="center">Utilization</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {optimizationResult.vehicleMix.map((mix) => (
                              <TableRow key={mix.type.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {mix.type.icon}
                                    {mix.type.name}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">{mix.count}</TableCell>
                                <TableCell align="center">{mix.routes}</TableCell>
                                <TableCell align="center">
                                  <LinearProgress
                                    variant="determinate"
                                    value={mix.utilization}
                                    sx={{ height: 8, borderRadius: 4 }}
                                  />
                                  <Typography variant="caption">
                                    {mix.utilization.toFixed(0)}%
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {optimizationResult.recommendations.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Recommendations
                          </Typography>
                          <List dense>
                            {optimizationResult.recommendations.map((rec, index) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  <CheckCircle color="success" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={rec} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                <Button onClick={() => setActiveStep(2)}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={runOptimization}
                  disabled={optimizationRunning}
                  startIcon={optimizationRunning ? <CircularProgress size={20} /> : <PlayArrow />}
                >
                  {optimizationRunning ? 'Optimizing...' : 'Run Optimization'}
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>

      {/* Comparison Dialog */}
      <Dialog
        open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Compare Delivery Options</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Option</TableCell>
                  <TableCell align="center">Cost</TableCell>
                  <TableCell align="center">Time</TableCell>
                  <TableCell align="center">Flexibility</TableCell>
                  <TableCell align="center">Control</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Private Fleet</TableCell>
                  <TableCell align="center">
                    <Chip label="Medium" color="warning" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Fast" color="success" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="High" color="success" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Full" color="success" size="small" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Carrier Services</TableCell>
                  <TableCell align="center">
                    <Chip label="Variable" color="default" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Moderate" color="warning" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Low" color="error" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Limited" color="error" size="small" />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Direct Shipping</TableCell>
                  <TableCell align="center">
                    <Chip label="High" color="error" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Fast" color="success" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Medium" color="warning" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Minimal" color="error" size="small" />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};